import React, { useState } from 'react';
import { X, ChefHat, ArrowRight, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

// Pages du menu, dans l'ordre de lecture du livret
const pages = [
  {
    id: 2,
    url: '/images/prestation-2.jpg',
    label: "L'apéritif",
    caption: 'Fait maison avec passion',
  },
  {
    id: 3,
    url: '/images/prestation-3.jpg',
    label: 'Nos plats à partager',
    caption: 'Des recettes généreuses et authentiques',
  },
  {
    id: 4,
    url: '/images/prestation-4.jpg',
    label: 'Saveurs du monde',
    caption: "Des plats généreux, d'ici et d'ailleurs",
  },
  {
    id: 5,
    url: '/images/prestation-5.jpg',
    label: 'Infos pratiques',
    caption: 'Accompagnements, déplacement & livraison',
  },
];

const PrestationsPage = () => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* Hero */}
      <section
        className="pt-36 pb-20 text-center"
        style={{ background: 'linear-gradient(to bottom right, hsl(0,0%,12%), hsl(0,64%,20%), hsl(0,0%,12%))' }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/20 rounded-full border-2 border-gold/40 mb-6">
            <ChefHat className="w-7 h-7 text-gold" />
          </div>
          <p className="text-gold font-medium mb-2 uppercase tracking-widest text-sm">Notre carte</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-cream mb-4">Nos Prestations</h1>
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <p className="text-cream/70 text-lg max-w-xl mx-auto">
            Apéritifs, plats à partager et saveurs du monde : découvrez tout ce que nous
            préparons pour vous, fait maison et sur place, du début à la fin de votre événement.
          </p>
        </div>
      </section>

      {/* Intro courte */}
      <section className="bg-white border-b border-gold/20 py-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-charcoal/70 leading-relaxed">
            Chaque formule ci-dessous peut être ajustée selon le nombre de convives, la saison
            et vos envies. Les tarifs indiqués sont par personne, hors frais de déplacement.
            Pour un devis précis et personnalisé, contactez-nous : c'est gratuit et sans engagement.
          </p>
        </div>
      </section>

      {/* Menu — galerie des pages */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
            {pages.map((page) => (
              <div key={page.id} className="group">
                <button
                  onClick={() => setSelected(page)}
                  className="block w-full text-left"
                  aria-label={`Agrandir : ${page.label}`}
                >
                  <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 border border-charcoal/5 bg-white">
                    <img
                      src={page.url}
                      alt={page.label}
                      className="w-full h-auto transform group-hover:scale-[1.03] transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                      <span className="px-5 py-2 bg-gold text-charcoal font-bold rounded-lg text-sm">
                        Agrandir
                      </span>
                    </div>
                  </div>
                </button>
                <div className="text-center mt-5">
                  <h2 className="text-xl font-bold text-charcoal">{page.label}</h2>
                  <p className="text-charcoal/60 text-sm mt-1">{page.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-charcoal text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-cream mb-4">Une envie en particulier ?</h2>
          <p className="text-cream/60 mb-8">
            Dites-nous-en plus sur votre événement et recevez un devis gratuit sous 24h.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/#booking"
              onClick={() => {
                setTimeout(() => {
                  const el = document.getElementById('booking');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all shadow-xl"
            >
              Demander un devis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+33663894103"
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-cream font-semibold rounded-lg border-2 border-cream/30 hover:border-gold hover:text-gold transition-all duration-200"
            >
              <PhoneCall className="w-5 h-5" />
              06 63 89 41 03
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-charcoal/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <button
            className="absolute top-4 right-4 text-cream hover:text-gold transition-colors"
            onClick={() => setSelected(null)}
            aria-label="Fermer"
          >
            <X size={32} />
          </button>
          <div className="max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-lg">
            <img
              src={selected.url}
              alt={selected.label}
              className="w-full h-auto rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PrestationsPage;
